import { useState, useEffect } from 'react';
import { View, Modal, Pressable, StyleSheet, ScrollView } from 'react-native';
import { Feather } from '@expo/vector-icons';
import DropDownPicker from 'react-native-dropdown-picker';
import Toast from 'react-native-root-toast';

import Util from '../util/util';
import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import UserRoles from './UserRoles';
import UserDefaultOrganization from './UserDefaultOrganization';
import ResetUserPassword from './ResetUserPassword';
import NotificationSubscriptionToggles from './NotificationSubscriptionToggles';
import AppButton from './AppButton';
import alert from './alert';
import { useTranslation } from '../i18n';

/**
 * UserDetailModal - Full-screen modal for viewing/editing user details
 * Used on compact screens where table columns are hidden
 */
const UserDetailModal = ({
  visible,
  onClose,
  username,
  user,
  validOrgs,
  onSaveRoleUpdate,
  onSaveOrgUpdate,
  onSaveOrgAccessUpdate,
  onSavePasswordReset,
  onEditUser,
  onDeleteUser,
}) => {
  const { t } = useTranslation(['admin', 'common']);

  const [updatingUser, setUpdatingUser] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Track pending changes so notification toggles update in real-time
  const [pendingDefaultOrg, setPendingDefaultOrg] = useState(null);
  const [pendingOrgAccess, setPendingOrgAccess] = useState(null);
  const [pendingRoles, setPendingRoles] = useState(null);
  // Escalation tier state
  const [escalationTierOpen, setEscalationTierOpen] = useState(false);
  const [escalationTier, setEscalationTier] = useState(user?.escalationTier || 1);

  // Reset pending state when user changes or modal opens
  useEffect(() => {
    if (user) {
      setPendingDefaultOrg(null);
      setPendingOrgAccess(null);
      setPendingRoles(null);
      setEscalationTier(user.escalationTier || 1);
    }
  }, [user, visible]);

  if (!user || !username) return null;

  // Calculate effective values (pending changes take precedence)
  const effectiveDefaultOrg = pendingDefaultOrg ?? user.organization;
  const effectiveOrgAccess = pendingOrgAccess ?? user.organization_access ?? [];
  const effectiveRoles = pendingRoles ?? user.roles ?? [];
  const isAnalyst = effectiveRoles.includes('Analyst');

  // currentOrgAccess for notification display: org_access + default org (always included)
  const currentOrgAccess = effectiveOrgAccess.includes(effectiveDefaultOrg)
    ? effectiveOrgAccess
    : [effectiveDefaultOrg, ...effectiveOrgAccess];

  // Wrapper to update local state and call parent handler for org access changes
  // Always ensures the effective default org is included
  const handleOrgAccessUpdate = (targetUsername, organizations) => {
    const orgs = organizations || [];
    // Default org must always be in org_access
    const withDefault = orgs.includes(effectiveDefaultOrg)
      ? orgs
      : [effectiveDefaultOrg, ...orgs];
    setPendingOrgAccess(withDefault);
    onSaveOrgAccessUpdate(targetUsername, withDefault);
  };

  // Wrapper to update local state when default org changes
  const handleDefaultOrgUpdate = (targetUsername, newOrg) => {
    setPendingDefaultOrg(newOrg);
    onSaveOrgUpdate(targetUsername, newOrg);
  };

  // Wrapper to update local state when roles change
  const handleRoleUpdate = (targetUsername, newRoles) => {
    setPendingRoles(newRoles);
    onSaveRoleUpdate(targetUsername, newRoles);
  };

  // Handler for escalation tier change
  const handleEscalationTierChange = async (tier) => {
    try {
      setEscalationTier(tier);
      await Util.patch('UserManagement', '/users/escalationTier', {
        queryParams: {
          email: username,
          organization: user.organization,
          escalationTier: tier,
        },
      });
      // Update the parent user object so the value persists across modal close/reopen
      user.escalationTier = tier;
      Toast.show(t('userDetail.tierUpdated'), {
        backgroundColor: AppStyles.colorSet.success,
        position: Toast.positions.BOTTOM,
        duration: 2000,
        textColor: AppStyles.colorSet.whiteColor,
      });
    } catch (error) {
      alert('Error', t('userDetail.tierUpdateFailed'));
      // Revert on error
      setEscalationTier(user.escalationTier || 1);
    }
  };

  const handleUpdate = async () => {
    setUpdatingUser(true);
    await onEditUser(username);
    setUpdatingUser(false);
    onClose();
  };

  const handleDelete = async () => {
    try {
      setUpdatingUser(true);
      await Util.post('UserManagement', '/users/delete', {
        body: {
          username: username,
          organization: user.organization,
        },
      });
      onDeleteUser(username);
      Toast.show(t('userDetail.userDeleted'), {
        backgroundColor: AppStyles.colorSet.success,
        position: Toast.positions.BOTTOM,
        duration: 2500,
        textColor: AppStyles.colorSet.whiteColor,
      });
      onClose();
    } catch (error) {
      alert('Error', t('userDetail.deleteFailed'));
    }
    setUpdatingUser(false);
    setConfirmDelete(false);
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <CustomText font="semiBold" size={18} style={styles.title}>
              {t('userDetail.title')}
            </CustomText>
            <Pressable
              onPress={onClose}
              style={({ hovered, focused }) => [
                styles.closeButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.closeButtonHovered,
              ]}
              accessibilityRole="button"
              accessibilityLabel={t('userDetail.closeLabel')}
            >
              <Feather name="x" size={24} color={AppStyles.colorSet.textSecondary} />
            </Pressable>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* Row 1: Email + Status side by side */}
            <View style={styles.row}>
              <View style={[styles.field, styles.fieldHalf]}>
                <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                  {t('userDetail.email')}
                </CustomText>
                <CustomText size={16} font="medium">
                  {username}
                </CustomText>
              </View>
              <View style={[styles.field, styles.fieldHalf]}>
                <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                  {t('userDetail.status')}
                </CustomText>
                <CustomText size={16} font="medium">
                  {t(`userStatus.${user.UserStatus}`, { defaultValue: user.UserStatus })}
                </CustomText>
              </View>
            </View>

            {/* Row 2: Roles + Reset Password side by side */}
            <View style={styles.row}>
              <View style={[styles.field, styles.fieldHalf]}>
                <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                  {t('userDetail.roles')}
                </CustomText>
                <UserRoles
                  user={username}
                  roles={user.roles}
                  edit={true}
                  postUpdateAction={handleRoleUpdate}
                />
              </View>
              <View style={[styles.field, styles.fieldHalf]}>
                <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                  {t('userDetail.resetPassword')}
                </CustomText>
                <ResetUserPassword
                  user={username}
                  organization={user.organization}
                  postUpdateAction={onSavePasswordReset}
                  fullWidth
                />
              </View>
            </View>

            {/* Row 2.5: Escalation Tier (only for Analyst users) */}
            {isAnalyst && (
              <View style={[styles.row, { zIndex: 3000 }]}>
                <View style={[styles.field, styles.fieldHalf, { zIndex: 3000 }]}>
                  <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                    {t('userDetail.escalationTier')}
                  </CustomText>
                  <DropDownPicker
                    open={escalationTierOpen}
                    value={escalationTier}
                    items={[
                      { label: t('userDetail.tier1'), value: 1 },
                      { label: t('userDetail.tier2'), value: 2 },
                      { label: t('userDetail.tier3'), value: 3 },
                    ]}
                    setOpen={setEscalationTierOpen}
                    setValue={(callback) => {
                      const newValue = typeof callback === 'function' ? callback(escalationTier) : callback;
                      handleEscalationTierChange(newValue);
                    }}
                    style={{
                      backgroundColor: AppStyles.colorSet.inputBackground,
                      borderColor: AppStyles.colorSet.inputBorder,
                      borderRadius: AppStyles.borderRadii.md,
                    }}
                    dropDownContainerStyle={{
                      backgroundColor: AppStyles.colorSet.inputBackground,
                      borderColor: AppStyles.colorSet.inputBorder,
                    }}
                    textStyle={{
                      fontFamily: 'Poppins-Regular',
                      fontSize: 15,
                      color: AppStyles.colorSet.textPrimary,
                    }}
                    zIndex={3000}
                    zIndexInverse={1000}
                  />
                  <CustomText size={11} color={AppStyles.colorSet.textMuted} style={{ marginTop: 4 }}>
                    {t('userDetail.tierHint')}
                  </CustomText>
                </View>
              </View>
            )}

            {/* Row 3: Default Organization (if multiple orgs) */}
            {validOrgs?.length > 1 && (
              <View style={[styles.row, { zIndex: 2000 }]}>
                <View style={[styles.field, styles.fieldHalf, { zIndex: 2000 }]}>
                  <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                    {t('userDetail.defaultOrganization')}
                  </CustomText>
                  <UserDefaultOrganization
                    user={username}
                    organization={user.organization}
                    organizations={validOrgs}
                    postUpdateAction={handleDefaultOrgUpdate}
                    multiple={false}
                    fullWidth
                  />
                </View>
              </View>
            )}

            {/* Row 4: Organization Access - full width (if multiple orgs) */}
            {validOrgs?.length > 1 && (
              <View style={[styles.field, { zIndex: 1500 }]}>
                <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                  {t('userDetail.organizationAccess')}
                </CustomText>
                <UserDefaultOrganization
                  user={username}
                  organization={user.organization_access}
                  organizations={validOrgs}
                  postUpdateAction={handleOrgAccessUpdate}
                  multiple={true}
                  fullWidth
                />
              </View>
            )}

            {/* Email Notification Preferences - only show for Analyst role */}
            {isAnalyst && (
              <View style={styles.field}>
                <CustomText size={12} color={AppStyles.colorSet.textSecondary} style={styles.label}>
                  {t('userDetail.emailNotifications')}
                </CustomText>
                <NotificationSubscriptionToggles
                  organizations={currentOrgAccess}
                  userEmail={username}
                  isAdmin={true}
                />
              </View>
            )}
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.actions}>
            <View style={styles.actionsRow}>
              <AppButton
                title={t('userDetail.saveChanges')}
                isBusy={updatingUser && !confirmDelete}
                isEnabled={!updatingUser}
                onPress={handleUpdate}
                buttonStyle={styles.updateButton}
              />
              <AppButton
                title={confirmDelete ? t('common:actions.confirm') : t('common:actions.delete')}
                isBusy={updatingUser && confirmDelete}
                isEnabled={!updatingUser}
                onPress={confirmDelete ? handleDelete : () => setConfirmDelete(true)}
                buttonStyle={[styles.deleteButton, confirmDelete && styles.deleteButtonConfirm]}
                textStyle={styles.deleteButtonText}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: AppStyles.spacing.xl,
  },
  modalContainer: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: AppStyles.borderRadii.lg,
    width: '100%',
    maxWidth: 750,
    maxHeight: '90%',
    ...AppStyles.styleSet.shadowLarge,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: AppStyles.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.borderSet.light,
  },
  title: {
    color: AppStyles.colorSet.textPrimary,
  },
  closeButton: {
    padding: AppStyles.spacing.xs,
    borderRadius: 8,
  },
  // Web hover/focus feedback for the close (X) button
  closeButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  content: {
    padding: AppStyles.spacing.lg,
  },
  row: {
    flexDirection: 'row',
    gap: AppStyles.spacing.lg,
  },
  field: {
    marginBottom: AppStyles.spacing.lg,
  },
  fieldHalf: {
    flex: 1,
  },
  label: {
    marginBottom: 6,
  },
  actions: {
    padding: AppStyles.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: AppStyles.borderSet.light,
  },
  actionsRow: {
    flexDirection: 'row',
    gap: AppStyles.spacing.sm,
  },
  updateButton: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.brandPrimary,
    borderRadius: AppStyles.borderRadii.md,
    padding: 8,
  },
  deleteButton: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.errorLight,
    borderRadius: AppStyles.borderRadii.md,
    padding: 8,
  },
  deleteButtonConfirm: {
    backgroundColor: AppStyles.colorSet.error,
  },
  deleteButtonText: {
    color: '#991B1B', // Dark red for text
  },
});

export default UserDetailModal;
